import React, { useEffect, useMemo } from 'react';
import { OnChangeValue, Props } from 'chakra-react-select';
import { Select, SelectOption } from './select';

type BasicSelectOption = SelectOption<string>;

/**
 * Just a select component using React Select without all the boilerplate
 */
function BasicSelect<IsMulti extends boolean = false>({
    isMulti = false as IsMulti,
    options,
    value,
    onChange,
    size,
    isLoading = false,
    isClearable = true,
    isLabelDropdown = false,
    isSearchable,
    placeholderColor,
    placeholder = 'Select an option',
    hidePlaceholderWhileLoading = false,
    stripUnknown = false,
    menuPosition = 'absolute',
    isDisabled,
    formatOptionLabel,
}: {
    onChange: Props<BasicSelectOption, IsMulti>['onChange'];
    formatOptionLabel?: Props<BasicSelectOption, IsMulti>['formatOptionLabel'];
    options: BasicSelectOption[];
    value: IsMulti extends true ? string[] : string | null;
    isMulti?: IsMulti;
    size?: React.ComponentProps<typeof Select>['size'];
    isClearable?: boolean;
    isLabelDropdown?: boolean;
    isSearchable?: boolean;
    placeholderColor?: string;
    placeholder?: string;
    isLoading?: boolean;
    hidePlaceholderWhileLoading?: boolean;
    isDisabled?: boolean;

    /**
     * Automatically clear the value if no matching property manager is found
     * Only single selects are supported
     */
    stripUnknown?: boolean;
    menuPosition?: 'absolute' | 'fixed';
}) {
    const selectedOption = useMemo(
        () => {
            if (isMulti) {
                return options.filter((option) => {
                    if (Array.isArray(value)) {
                        return value.includes(option.value);
                    }

                    return option.value === value;
                });
            }

            return options.find((option) => option.value === value);
        },
        [options, value, isMulti]
    );

    // If the value is non-null and no existing property manager is found,
    // stripUnknown will clear the value
    useEffect(() => {
        if (!stripUnknown || isMulti) return;

        if (isLoading) return;

        if (value === null) return;

        if (selectedOption !== undefined) return;

        if (onChange !== undefined) {
            onChange(
                null as OnChangeValue<BasicSelectOption, IsMulti>,
                { action: 'clear', removedValues: [] }
            );
        }
    }, [
        isMulti,
        stripUnknown,
        selectedOption,
        value,
        options,
        isLoading,
    ]);

    return (
        <Select<BasicSelectOption, IsMulti>
            isClearable={isClearable}
            options={options}
            onChange={onChange}
            formatOptionLabel={formatOptionLabel}
            value={selectedOption}
            isLoading={isLoading}
            placeholder={(hidePlaceholderWhileLoading && isLoading) ? '' : placeholder}
            isMulti={isMulti}
            size={size}
            isLabelDropdown={isLabelDropdown}
            isSearchable={isSearchable}
            placeholderColor={placeholderColor}
            menuPosition={menuPosition}
            isDisabled={isDisabled}
        />
    );
}

export { BasicSelect };
